import React, { useState, useEffect, FC } from "react";
import { Link } from "react-router-dom";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Badge,
  Modal,
  ModalBody,
  Row,
  Col,
} from "reactstrap";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
  Button,
  Icon,
  PaginationComponent,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  TooltipComponent,
} from "../../../components/Component";
import Swal from "sweetalert2";
import workUomApi from "../../../api/master/workUom";
import sequenceApis from "../../../api/master/sequence";
import { useSelector } from "react-redux";
import moment from "moment";
import AddModal from "./AddModalWorkUom";
import Export from "../../../components/export/Export";
import { Empty } from "antd";

interface WorkUomItem {
  id: string;
  paginationId: number;
  referenceID: string;
  workUomID: string;
  workUomName: string;
  uomName: string;
  description?: string;
  status: string;
  createdDate: string;
}

const WorkUom: FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { data: accessibility } = useSelector((state: any) => state.accessibility);
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const [allowedActions, setAllowedActions] = useState<any>({
    add: 0,
    view: 0,
    edit: 0,
    approve: 0,
    reject: 0,
    cancel: 0,
  });
  const [onSearchText, setSearchText] = useState("");
  const [itemPerPage, setItemPerPage] = useState(10);
  const [sort, setSortState] = useState("DESC");
  const [sm, updateSm] = useState(false);
  const [isSequenceSet, setIsSequenceSet] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    sortOrder: "DESC" as "ASC" | "DESC",
    direction: "P" as "P" | "F" | "B",
    pageDiff: 0,
    paginationId: null as number | null,
  });

  const [modal, setModal] = useState({
    add: false,
  });
  const [viewModal, setViewModal] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    type: "",
    message: "",
    errors: [],
  });
  const [detail, setDetail] = useState<WorkUomItem | null>(null);
  const [formData, setFormData] = useState({
    workUomSequenceId: "",
    uomName: "",
    uomShortCode: "",
    uomDescription: "",
    uomType: "",
  });
  const [rowData, setRowData] = useState<WorkUomItem[]>([]);
  const [originalData, setOriginalData] = useState<WorkUomItem[]>([]);

  const loadDetail = async (id: string) => {
    try {
      // Convert id to number for API call
      const numericId = parseInt(id, 10);
      if (isNaN(numericId)) {
        Swal.fire("Error", "Invalid Work UOM ID", "error");
        return;
      }

      const response = await workUomApi.fetchWorkUomById(numericId);
      if (response.status && response.data) {
        const item = response.data;
        setDetail({
          id: item.paginationId?.toString() || item.workUomSequenceId?.toString() || id,
          paginationId: item.paginationId,
          referenceID: item.workUomSequenceId || "",
          workUomID: item.uomType || "",
          workUomName: item.uomType || "",
          uomName: item.uomName || "",
          description: item.uomDescription || "",
          status: item.status || "Active",
          createdDate: item.createdDate || "",
        });
      } else {
        Swal.fire("Error", response.message || "Failed to fetch Work UOM details", "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.response?.data?.message || error.message || "Failed to fetch Work UOM details", "error");
    }
  };

  const closeModal = () => {
    setModal({ ...modal, add: false });
    resetForm();
    setAlertInfo({
      type: "",
      message: "",
      errors: [],
    });
  };


  const resetForm = () => {
    setFormData({
      workUomSequenceId: "",
      uomName: "",
      uomShortCode: "",
      uomDescription: "",
      uomType: "",
    });
  };

  const onFormSubmit = async (submitData: any) => {
    const response = await workUomApi.createWorkUom(submitData);
    const { status, message } = response;
    if (status) {
      Swal.fire("Created!", "Work UOM has been created", "success");
      setAlertInfo({
        type: "",
        message: "",
        errors: [],
      });
      closeModal();
      fetchData();
    } else {
      let errorMessage = "An error occurred";
      const fullErrorMessage = `${errorMessage}\n${message.join(", ")}`;
      setAlertInfo({
        type: "error",
        message: fullErrorMessage,
        errors: [],
      });
    }
  };


  const onFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleStatusChange = async (workUomId: any, action: "activate" | "inactivate") => {
    const actionText = action === "activate" ? "Activate" : "Inactivate";

    Swal.fire({
      title: `Are you sure?`,
      text: `You want to ${actionText} this Work UOM!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, ${actionText} it!`,
      allowOutsideClick: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-primary",
        cancelButton: "btn btn-secondary",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Convert id to number
          const numericId = parseInt(workUomId, 10);
          if (isNaN(numericId)) {
            Swal.fire("Error", "Invalid Work UOM ID", "error");
            return;
          }

          const response =
            action === "activate"
              ? await workUomApi.activateWorkUomById(numericId)
              : await workUomApi.inactivateWorkUomById(numericId);

          if (response.status) {
            await Swal.fire(
              "Success!",
              `Work UOM has been ${action === "activate" ? "Activated" : "Inactivated"}.`,
              "success"
            );
            fetchData();
          } else {
            await Swal.fire("Error", response.message || "An error occurred", "error");
          }
        } catch (error: any) {
          await Swal.fire("Error", error.response?.data?.message || error.message || "An error occurred", "error");
        }
      }
    });
  };

  useEffect(() => {
    if (accessibility) {
      try {
        const { add, view, edit, approve, reject, cancel } = accessibility["Master"];
        setAllowedActions({ add, view, edit, approve, reject, cancel });
      } catch (error) {
        console.log(error);
      }
    }
  }, [accessibility]);

  // Check if sequence is set for Work UOM
  useEffect(() => {
    fetchSequenceAccess();
  }, []);

  const fetchSequenceAccess = async () => {
    try {
      const response = await sequenceApis.fetchSequence();
      const { data } = response;
      const today = new Date();
      // Check if sequence exists with:
      // 1. moduleName === "Work UOM"
      // 2. moduleId === [Work UOM moduleId] (add specific moduleId if needed)
      // 3. status === "Active" (if "Inactive", Work UOM Sequence ID field will be shown)
      // 4. endDate is in the future
      const _sequenceAccess = data?.filter(
        (item: any) =>
          item.moduleName === "Work UOM" &&
          item.status === "Active" &&
          new Date(item.endDate) > today
      );
      setIsSequenceSet(_sequenceAccess && _sequenceAccess.length > 0);
    } catch (error: any) {
      console.log(error.response?.data?.message || "An error occurred");
      setIsSequenceSet(false);
    }
  };

  const fetchData = async (params: any = {}) => {
    setIsLoading(true);
    try {
      // Get siteid from currentUser's siteAccessibility
      let siteid = null;
      if (currentUser?.siteAccessibility && currentUser.siteAccessibility.length > 0) {
        const siteIds = currentUser.siteAccessibility.map((item: any) => item.siteID);
        siteid = siteIds.join(",");
      }

      const apiParams = {
        sortOrder: params.sortOrder || pagination.sortOrder,
        direction: params.direction || pagination.direction,
        pageSize: params.pageSize || pagination.pageSize,
        pageDiff: params.pageDiff || pagination.pageDiff,
        paginationId: params.paginationId !== undefined ? params.paginationId : pagination.paginationId,
        searchQuery: params.searchQuery !== undefined ? params.searchQuery : onSearchText,
        id: siteid,
      };

      const { data, status, message } = await workUomApi.fetchWorkUomWithPagination(apiParams);
      
      if (status) {
        const { list, count } = data;
        
        // Map API response to component structure
        const mappedData = (list || []).map((item: any) => ({
          id: item.paginationId?.toString() || item.workUomSequenceId,
          paginationId: item.paginationId,
          referenceID: item.workUomSequenceId,
          workUomID: item.uomType || "",
          workUomName: item.uomType || "",
          uomName: item.uomName,
          description: item.uomDescription || "",
          status: item.status,
          createdDate: item.createdDate,
        }));

        setRowData(mappedData);
        setOriginalData(mappedData);
        
        setPagination((prev) => ({
          ...prev,
          total: count || 0,
          ...(params.current && { current: params.current }),
          ...(params.pageSize && { pageSize: params.pageSize }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
          ...(params.direction && { direction: params.direction }),
          ...(params.pageDiff !== undefined && { pageDiff: params.pageDiff }),
          ...(params.paginationId !== undefined && {
            paginationId: params.paginationId,
          }),
        }));
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (err: any) {
      Swal.fire("Error", err.response?.data?.message || err.message, "error");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    const pageDiff =
      pagination.direction === "P"
        ? pagination.current - 1
        : 0;

    const apiParams = {
      sortOrder: pagination.sortOrder,
      pageSize: pagination.pageSize.toString(),
      direction: pagination.direction,
      pageDiff,
      paginationId: pagination.paginationId,
      searchQuery: onSearchText,
    };
    fetchData(apiParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, pagination.sortOrder, pagination.direction, pagination.paginationId, pagination.pageDiff, onSearchText]);

  useEffect(() => {
    // Reset to first page when search text changes
    setPagination((prev) => ({
      ...prev,
      current: 1,
      paginationId: null,
      direction: "P",
      pageDiff: 0,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSearchText]);

  const sortFunc = (params: any) => {
    const sortOrder = params === "asc" ? "ASC" : "DESC";
    setPagination((prev) => ({
      ...prev,
      sortOrder,
      current: 1,
      paginationId: null,
      direction: "P",
      pageDiff: 0,
    }));
    setSortState(params);
  };

  return (
    <>
      <Head title="Work UOM"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Work UOM
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>You have total {pagination.total} Work UOMs.</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <Button
                  className={`btn-icon btn-trigger toggle-expand me-n1 ${
                    sm ? "active" : ""
                  }`}
                  onClick={() => updateSm(!sm)}
                >
                  <Icon name="menu-alt-r"></Icon>
                </Button>
                <div
                  className="toggle-expand-content"
                  style={{ display: sm ? "block" : "none" }}
                >
                  <ul className="nk-block-tools g-3">
                    <li>
                      <Export data={rowData} />
                    </li>
                    {allowedActions?.add && (
                      <li className="nk-block-tools-opt">
                        <Button
                          color="primary"
                          onClick={() => {
                            setModal({ ...modal, add: true });
                            setAlertInfo({
                              type: "",
                              message: "",
                              errors: [],
                            });
                          }}
                        >
                          <Icon name="plus"></Icon>
                          <span>Add</span>
                        </Button>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <Block>
          <DataTable
            className="card-stretch"
            bodyClassName={undefined}
            title={undefined}
          >
            <div className="card-inner position-relative card-tools-toggle">
              <div className={`card-search search-wrap active`}>
                <div className="card-body">
                  <div className="search-content" style={{ whiteSpace: "nowrap", display: "flex" }}>
                    <Button
                      className="search-back btn-icon toggle-search active"
                    >
                      <Icon name="search"></Icon>
                    </Button>
                    <input
                      type="text"
                      className="border-transparent form-focus-none form-control"
                      placeholder="Search"
                      value={onSearchText}
                      onChange={(e) => onFilterChange(e)}
                    />
                    <UncontrolledDropdown>
                      <DropdownToggle
                        tag="a"
                        className="btn btn-trigger btn-icon dropdown-toggle"
                      >
                        <Icon name="filter"></Icon>
                      </DropdownToggle>
                      <DropdownMenu end className="dropdown-menu-xs">
                        <ul className="link-check">
                          <li>
                            <span>Show</span>
                          </li>
                          <li className={pagination.pageSize === 10 ? "active" : ""}>
                            <DropdownItem
                              tag="a"
                              href="#dropdownitem"
                              onClick={(ev) => {
                                ev.preventDefault();
                                setPagination((prev) => ({
                                  ...prev,
                                  pageSize: 10,
                                  current: 1,
                                  paginationId: null,
                                  direction: "P",
                                  pageDiff: 0,
                                }));
                                setItemPerPage(10);
                              }}
                            >
                              10
                            </DropdownItem>
                          </li>
                          <li className={pagination.pageSize === 15 ? "active" : ""}>
                            <DropdownItem
                              tag="a"
                              href="#dropdownitem"
                              onClick={(ev) => {
                                ev.preventDefault();
                                setPagination((prev) => ({
                                  ...prev,
                                  pageSize: 15,
                                  current: 1,
                                  paginationId: null,
                                  direction: "P",
                                  pageDiff: 0,
                                }));
                                setItemPerPage(15);
                              }}
                            >
                              15
                            </DropdownItem>
                          </li>
                        </ul>
                        <ul className="link-check">
                          <li>
                            <span>Order</span>
                          </li>
                          <li className={sort === "dsc" || pagination.sortOrder === "DESC" ? "active" : ""}>
                            <DropdownItem
                              tag="a"
                              href="#dropdownitem"
                              onClick={(ev) => {
                                ev.preventDefault();
                                setSortState("dsc");
                                sortFunc("dsc");
                              }}
                            >
                              DESC
                            </DropdownItem>
                          </li>
                          <li className={sort === "asc" || pagination.sortOrder === "ASC" ? "active" : ""}>
                            <DropdownItem
                              tag="a"
                              href="#dropdownitem"
                              onClick={(ev) => {
                                ev.preventDefault();
                                setSortState("asc");
                                sortFunc("asc");
                              }}
                            >
                              ASC
                            </DropdownItem>
                          </li>
                        </ul>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </div>
                </div>
              </div>
            </div>

            <DataTableBody compact className={undefined} bodyclass={undefined}>
              <DataTableHead>
                <DataTableRow size="md" className={undefined}>
                  <span className="sub-text">Work UOM ID</span>
                </DataTableRow>
                <DataTableRow size="md" className={undefined}>
                  <span className="sub-text">Work UOM</span>
                </DataTableRow>
                <DataTableRow size="md" className={undefined}>
                  <span className="sub-text">UOM Name</span>
                </DataTableRow>
                <DataTableRow size="sm" className={undefined}>
                  <span className="sub-text">Description</span>
                </DataTableRow>
                <DataTableRow size="md" className={undefined}>
                  <span className="sub-text">Created Date</span>
                </DataTableRow>
                <DataTableRow size="md" className={undefined}>
                  <span className="sub-text">Status</span>
                </DataTableRow>
                <DataTableRow size="md" className={undefined}>
                  {/* <span className="sub-text">Details</span> */}
                </DataTableRow>
              </DataTableHead>
              {isLoading ? (
                <div className="text-center p-4">
                  <span className="text-silent">Loading...</span>
                </div>
              ) : rowData.length > 0 ? (
                rowData.map((item: any, index: number) => {
                  return (
                    <DataTableItem
                      key={item.id}
                      className={undefined}
                      style={{ color: "black" }}
                    >
                      <DataTableRow size="md" className={undefined}>
                        <span>{item.referenceID}</span>
                      </DataTableRow>
                      <DataTableRow size="md" className={undefined}>
                        <span>{item.workUomName}</span>
                      </DataTableRow>
                      <DataTableRow size="md" className={undefined}>
                        <span>{item.uomName}</span>
                      </DataTableRow>
                      <DataTableRow size="sm" className={undefined}>
                        <span id={`desc-tooltip-${index}`}>
                          {item.description && item.description.length > 25
                            ? `${item.description.substring(0, 25)}...`
                            : item.description || "-"}
                        </span>
                        {item.description && item.description.length > 25 && (
                          <TooltipComponent
                            iconClass="info-circle text-info"
                            icon="info"
                            id={`desc-tooltip-${index}`}
                            direction="top"
                            text={item.description}
                            containerClassName="d-inline-block ms-1"
                          />
                        )}
                      </DataTableRow>
                      <DataTableRow size="md" className={undefined}>
                        <span>{moment(item.createdDate).format("DD-MMM-YYYY")}</span>
                      </DataTableRow>
                      <DataTableRow size="md" className={undefined}>
                        <span
                          className={`tb-status text-${
                            item.status === "Active" ? "success" : "danger"
                          }`}
                        >
                          {item.status}
                        </span>
                      </DataTableRow>
                      <DataTableRow size="md" className={undefined}>
                        <ul className="nk-tb-actions">
                          <li>
                            <UncontrolledDropdown>
                              <DropdownToggle
                                tag="a"
                                className="dropdown-toggle btn btn-icon btn-trigger"
                              >
                                <Icon name="more-h"></Icon>
                              </DropdownToggle>
                              <DropdownMenu end>
                                <ul className="link-list-opt no-bdr">
                                  {allowedActions?.view && (
                                    <li>
                                      <Link
                                        to="#"
                                        onClick={async () => {
                                          await loadDetail(item.id);
                                          setViewModal(true);
                                        }}
                                      >
                                        <Icon name="focus"></Icon>
                                        <span>View Details</span>
                                      </Link>
                                    </li>
                                  )}
                                  {item.status === "Active" ? (
                                    <li>
                                      <DropdownItem
                                        tag="a"
                                        href="#inactivate"
                                        onClick={(ev) => {
                                          ev.preventDefault();
                                          handleStatusChange(item.id, "inactivate");
                                        }}
                                      >
                                        <Icon name="cross-circle" />
                                        <span>Inactivate</span>
                                      </DropdownItem>
                                    </li>
                                  ) : (
                                    <li>
                                      <DropdownItem
                                        tag="a"
                                        href="#activate"
                                        onClick={(ev) => {
                                          ev.preventDefault();
                                          handleStatusChange(item.id, "activate");
                                        }}
                                      >
                                        <Icon name="check-circle" />
                                        <span>Activate</span>
                                      </DropdownItem>
                                    </li>
                                  )}
                                </ul>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </li>
                        </ul>
                      </DataTableRow>
                    </DataTableItem>
                  );
                })
              ) : (
                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "200px",
                    backgroundColor: "white",
                  }}>
                    <Empty description="No Data Found " />
                  </div>
              )}
            </DataTableBody>
            <div className="card-inner">
              {rowData.length > 0 ? (
                <PaginationComponent
                  itemPerPage={pagination.pageSize}
                  totalItems={pagination.total}
                  paginate={(pageNumber: number) => {
                    const direction = pageNumber > pagination.current ? "F" : "B";
                    const pageDiff = Math.abs(pageNumber - pagination.current) - 1;
                    let paginationId: number | null = null;
                    
                    if (direction === "F" && rowData.length > 0) {
                      paginationId = rowData[rowData.length - 1]?.paginationId || null;
                    } else if (direction === "B" && rowData.length > 0) {
                      paginationId = rowData[0]?.paginationId || null;
                    }
                    
                    setPagination((prev) => ({
                      ...prev,
                      current: pageNumber,
                      direction,
                      pageDiff,
                      paginationId,
                    }));
                  }}
                  currentPage={pagination.current}
                />
              ) : null}
            </div>
          </DataTable>
        </Block>
      </Content>

      <AddModal
        modal={modal.add}
        formData={formData}
        setFormData={setFormData}
        closeModal={closeModal}
        onSubmit={onFormSubmit}
        filterStatus
        alertInfo={alertInfo}
        isSequenceSet={isSequenceSet}
      />

      <Modal
        isOpen={viewModal}
        toggle={() => setViewModal(false)}
        className="modal-dialog-centered"
        size="lg"
      >
        <ModalBody>
          <a
            href="#cancel"
            onClick={(ev) => {
              ev.preventDefault();
              setViewModal(false);
            }}
            className="close"
          >
            <Icon name="cross-sm"></Icon>
          </a>
          <div className="nk-modal-head">
            <h4 className="nk-modal-title title">
              Work UOM Details -{" "}
              <small className="text-primary"> {detail?.referenceID}</small>
            </h4>
          </div>
          <div className="nk-tnx-details mt-sm-3">
            <Row className="gy-3">
              <Col lg={6}>
                <span className="sub-text">Work UOM ID</span>
                <span className="caption-text">{detail?.referenceID}</span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Work Category</span>
                <span className="caption-text text-break">
                  {detail?.workUomName}
                </span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">UOM Name</span>
                <span className="caption-text">{detail?.uomName}</span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Status</span>
                <Badge
                  color={detail?.status === "Active" ? "success" : "danger"}
                  size="md"
                >
                  {detail?.status}
                </Badge>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Description</span>
                <div className="d-flex align-items-center">
                  <span 
                    id="view-desc-tooltip"
                    className="caption-text"
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                      display: "inline-block"
                    }}
                  >
                    {detail?.description || "-"}
                  </span>
                  {detail?.description && detail.description.length > 30 && (
                    <TooltipComponent
                      iconClass="info-circle text-info ms-1"
                      icon="info"
                      id="view-desc-tooltip"
                      direction="top"
                      text={detail.description}
                      containerClassName="d-inline-block"
                    />
                  )}
                </div>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Created Date</span>
                <span className="caption-text">
                  {" "}
                  {detail?.createdDate && moment(detail.createdDate).format("DD MMM YYYY")}
                </span>
              </Col>
            </Row>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default WorkUom;

